import type { BookingResponse, ObjectApartmentSummary } from '@/shared/api';
import { lazy, Suspense } from 'react';
import { BookingReassignmentDialog } from '../reassignment';
import { UpgradeChargeDialog, useUpgradeCharges } from '../upgrade-charge';
import type { BookingActionKind } from './BookingActionList';
import type { BookingActionFlags } from './bookingActionRules';
import { CancelBookingDialog } from './CancelBookingDialog';
import { EditBookingModal } from './EditBookingModal';
import { OtaEarlyDepartureDialog } from './OtaEarlyDepartureDialog';
import type { useOtaEarlyDeparture } from './useOtaEarlyDeparture';

const BookingMessageDialog = lazy(() =>
  import('./BookingMessageDialog').then((module) => ({ default: module.BookingMessageDialog })),
);

interface Props {
  booking: BookingResponse;
  flags: BookingActionFlags;
  open: BookingActionKind | null;
  onClose: () => void;
  guestName: string;
  propertyName: string | null;
  properties: ObjectApartmentSummary[];
  earlyDeparture: ReturnType<typeof useOtaEarlyDeparture>;
}

/** Alle Dialoge der Aktionen an einer Stelle – sie bleiben offen, auch wenn das Aktionsblatt schließt. */
export function BookingActionDialogs({
  booking,
  flags,
  open,
  onClose,
  guestName,
  propertyName,
  properties,
  earlyDeparture,
}: Props) {
  const upgrade = useUpgradeCharges(booking.id, false);
  const change = (kind: BookingActionKind) => (next: boolean) =>
    !next && open === kind && onClose();
  return (
    <>
      {flags.message && open === 'message' && (
        <Suspense fallback={null}>
          <BookingMessageDialog
            key={booking.id}
            bookingId={booking.id}
            channel={booking.channel ?? ''}
            onClose={onClose}
          />
        </Suspense>
      )}
      {flags.upgrade && (
        <UpgradeChargeDialog
          open={open === 'upgrade'}
          currency={booking.currency}
          pending={upgrade.request.isPending}
          onOpenChange={change('upgrade')}
          onSubmit={(request) => upgrade.request.mutate(request, { onSuccess: onClose })}
        />
      )}
      {flags.editOrCancel && (
        <EditBookingModal
          open={open === 'edit'}
          onOpenChange={change('edit')}
          booking={booking}
          guestName={guestName}
          propertyName={propertyName}
        />
      )}
      {(flags.editOrCancel || flags.finishCancel) && (
        <CancelBookingDialog
          open={open === 'cancel'}
          onOpenChange={change('cancel')}
          bookingId={booking.id}
          guestName={guestName}
          checkInDate={booking.checkInDate}
          checkOutDate={booking.checkOutDate}
        />
      )}
      {(flags.earlyDeparture || flags.planEditable) && (
        <OtaEarlyDepartureDialog
          open={open === 'early'}
          onOpenChange={change('early')}
          checkOutDate={booking.checkOutDate}
          departureDate={earlyDeparture.departureDate}
          mode={earlyDeparture.mode}
          editingPlan={!!flags.plan}
          dateError={earlyDeparture.dateError}
          reason={earlyDeparture.reason}
          refundAmount={earlyDeparture.refundAmount}
          refundReason={earlyDeparture.refundReason}
          ledger={earlyDeparture.ledger}
          pending={earlyDeparture.pending}
          canSubmit={earlyDeparture.canSubmit}
          onDateChange={earlyDeparture.setDepartureDate}
          onReasonChange={earlyDeparture.setReason}
          onRefundAmountChange={earlyDeparture.setRefundAmount}
          onRefundReasonChange={earlyDeparture.setRefundReason}
          onSubmit={earlyDeparture.submit}
        />
      )}
      {flags.reassign && (
        <BookingReassignmentDialog
          open={open === 'reassign'}
          onOpenChange={change('reassign')}
          bookingId={booking.id}
          sourcePropertyId={booking.propertyId}
          sourcePropertyName={propertyName ?? 'Aktuelles Apartment'}
          checkInDate={booking.checkInDate}
          checkOutDate={booking.checkOutDate}
          properties={properties}
        />
      )}
    </>
  );
}
